import type { LocationTreeNode } from '../../../api/schedulingApis';
import { findNodeById, isProjectRootNode } from './locationManagementUtils';

export function normalizeLocationTypeKey(type: string | null | undefined): string {
  return String(type ?? '')
    .trim()
    .toLowerCase();
}

/**
 * Trim free-text location type input. When it matches an existing value
 * case-insensitively, return that existing canonical value; otherwise return the trim.
 */
export function resolveLocationTypeInput(
  raw: string | null | undefined,
  existingValues?: Iterable<string>,
): string {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return '';
  if (existingValues) {
    const key = normalizeLocationTypeKey(trimmed);
    for (const candidate of existingValues) {
      if (normalizeLocationTypeKey(candidate) === key) return candidate;
    }
  }
  return trimmed;
}

export function isProjectLocationType(type: string | null | undefined): boolean {
  return normalizeLocationTypeKey(type) === 'project';
}

export function isBlockLocationType(type: string | null | undefined): boolean {
  return normalizeLocationTypeKey(type) === 'block';
}

export function isFloorLocationType(type: string | null | undefined): boolean {
  return normalizeLocationTypeKey(type) === 'floor';
}

export function getFloorNumberFromMeta(
  meta: Record<string, unknown> | null | undefined,
): number | null {
  if (!meta || typeof meta !== 'object') return null;
  const raw = meta.floor_number ?? meta.floorNumber;
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * Next floor index under a parent for sequential floor creation/clone.
 * If no sibling floor has an index, start at 0; otherwise max(index) + 1.
 */
export function nextFloorIndexUnderParent(siblings: LocationTreeNode[]): number {
  const indices = siblings
    .filter((n) => isFloorLocationType(n.location_type))
    .map((n) => getFloorNumberFromMeta(n.meta))
    .filter((n): n is number => n != null);
  if (indices.length === 0) return 0;
  return Math.max(...indices) + 1;
}

export function withFloorNumberMeta(
  meta: Record<string, unknown> | null | undefined,
  floorNumber: number | null,
): Record<string, unknown> {
  const next = { ...(meta ?? {}) };
  if (floorNumber == null || !Number.isFinite(floorNumber)) {
    delete next.floor_number;
    delete next.floorNumber;
    return next;
  }
  next.floor_number = floorNumber;
  delete next.floorNumber;
  return next;
}

export function countProjectLocationNodes(
  nodes: LocationTreeNode[],
  excludeId?: string | null,
): number {
  let count = 0;
  const walk = (list: LocationTreeNode[]) => {
    for (const n of list) {
      if (excludeId != null && String(n.location_id) === String(excludeId)) {
        if (n.child?.length) walk(n.child);
        continue;
      }
      if (isProjectLocationType(n.location_type)) count += 1;
      if (n.child?.length) walk(n.child);
    }
  };
  walk(nodes);
  return count;
}

/**
 * Project-type locations: deletable only when more than one exists (cleanup duplicates).
 * Other structural roots remain protected. Non-root non-project nodes are deletable.
 */
export function canDeleteLocationNode(
  node: LocationTreeNode | null | undefined,
  tree: LocationTreeNode[],
): boolean {
  if (!node) return false;
  if (isProjectLocationType(node.location_type)) {
    return countProjectLocationNodes(tree) > 1;
  }
  if (isProjectRootNode(node)) return false;
  return true;
}

export type LocationTypePlacementResult = { ok: true } | { ok: false; reason: string };

/**
 * Location type hierarchy rules:
 * - Project: only one, and only as root
 * - Block: cannot be a child of block or floor
 * - Floor: cannot be a child of floor (can be under block / other non-floor parents)
 */
export function canPlaceLocationType(params: {
  childType: string;
  parent: LocationTreeNode | null;
  tree: LocationTreeNode[];
  /** When changing type / moving an existing node, exclude it from the project uniqueness count. */
  excludeNodeId?: string | null;
  /** When true, parent===null means project root placement. */
  asRoot?: boolean;
}): LocationTypePlacementResult {
  const type = normalizeLocationTypeKey(params.childType);
  const parent = params.parent;
  const parentType = parent ? normalizeLocationTypeKey(parent.location_type) : null;
  const placingAsRoot = params.asRoot === true || parent == null;

  if (type === 'project') {
    if (!placingAsRoot) {
      return { ok: false, reason: 'Project type is only allowed on the root location.' };
    }
    if (countProjectLocationNodes(params.tree, params.excludeNodeId) > 0) {
      return { ok: false, reason: 'There can only be one Project location, and it must be the root.' };
    }
    return { ok: true };
  }

  if (placingAsRoot && type !== 'project') {
    // When a Project root exists, nothing may sit beside/above it as another root.
    // Nodes must be placed under the Project (or under its descendants).
    if (countProjectLocationNodes(params.tree, params.excludeNodeId) > 0) {
      return {
        ok: false,
        reason: 'Locations must sit under the Project root; nothing can be placed above it.',
      };
    }
  }

  if (type === 'block') {
    if (parentType === 'block' || parentType === 'floor') {
      return {
        ok: false,
        reason: 'Block cannot be a child of another Block or Floor location.',
      };
    }
    return { ok: true };
  }

  if (type === 'floor') {
    if (parentType === 'floor') {
      return { ok: false, reason: 'Floor cannot be a child of another Floor location.' };
    }
    return { ok: true };
  }

  return { ok: true };
}

export type AnnotatedLocationTypeOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

/**
 * Keep all options visible; mark placement-invalid types as disabled so users
 * see available switches without trial-and-error errors.
 */
export function annotateLocationTypeOptions(
  options: Array<{ value: string; label: string }>,
  parent: LocationTreeNode | null,
  tree: LocationTreeNode[],
  opts?: { excludeNodeId?: string | null; includeProject?: boolean },
): AnnotatedLocationTypeOption[] {
  return options.map((o) => {
    const key = normalizeLocationTypeKey(o.value);
    if (key === 'project' && !opts?.includeProject) {
      return { ...o, disabled: true };
    }
    const result = canPlaceLocationType({
      childType: o.value,
      parent,
      tree,
      excludeNodeId: opts?.excludeNodeId,
      asRoot: parent == null,
    });
    return result.ok ? { ...o, disabled: false } : { ...o, disabled: true };
  });
}

/** Filter creatable type options for a node under the given parent. */
export function filterAllowedLocationTypes(
  options: Array<{ value: string; label: string }>,
  parent: LocationTreeNode | null,
  tree: LocationTreeNode[],
  opts?: { excludeNodeId?: string | null; includeProject?: boolean },
): Array<{ value: string; label: string }> {
  return annotateLocationTypeOptions(options, parent, tree, opts).filter((o) => !o.disabled);
}

export function findParentNode(
  tree: LocationTreeNode[],
  node: LocationTreeNode,
): LocationTreeNode | null {
  const nodeId = node.location_id != null ? String(node.location_id) : '';
  if (!nodeId) return null;

  let structuralParent: LocationTreeNode | null = null;
  let found = false;
  const walk = (list: LocationTreeNode[], immediateParent: LocationTreeNode | null) => {
    for (const n of list) {
      if (String(n.location_id) === nodeId) {
        structuralParent = immediateParent;
        found = true;
        return;
      }
      if (n.child?.length) walk(n.child, n);
      if (found) return;
    }
  };
  walk(tree, null);
  if (found) return structuralParent;

  if (node.parent_location_id == null) return null;
  return findNodeById(tree, String(node.parent_location_id));
}

/** Sort floor siblings by floor index ascending; others keep source order. */
export function sortFloorSiblings(children: LocationTreeNode[]): LocationTreeNode[] {
  return children
    .map((node, originalIndex) => ({ node, originalIndex }))
    .sort((a, b) => {
      const aFloor = isFloorLocationType(a.node.location_type)
        ? getFloorNumberFromMeta(a.node.meta)
        : null;
      const bFloor = isFloorLocationType(b.node.location_type)
        ? getFloorNumberFromMeta(b.node.meta)
        : null;
      if (aFloor != null && bFloor != null) return aFloor - bFloor;
      return a.originalIndex - b.originalIndex;
    })
    .map(({ node }) => node);
}
